/**
 * Declarative form schema. No tenant-supplied code is ever evaluated: visibility and
 * requiredness are expressed as ConditionGroup data and interpreted by conditions.ts.
 */

export const FIELD_TYPES = [
  'short_text',
  'long_text',
  'email',
  'phone',
  'url',
  'integer',
  'decimal',
  'currency',
  'date',
  'year',
  'single_select',
  'multi_select',
  'yes_no',
  'file_upload',
  'asset_link',
  'repeater',
  'weekly_hours',
  'contact_selector',
  'acknowledgement',
  'signature',
  /** Non-input instruction/notice block used to preserve source wording. */
  'info',
] as const;

export type FieldType = (typeof FIELD_TYPES)[number];

export const INPUT_FIELD_TYPES = FIELD_TYPES.filter((t) => t !== 'info' && t !== 'repeater');

export type Sensitivity = 'normal' | 'confidential' | 'restricted';

export const SHARED_PROFILE_KEYS = [
  'legal_name',
  'public_brand',
  'industry',
  'primary_contact_name',
  'primary_contact_designation',
  'email',
  'phone',
  'website',
  'year_established',
  'business_description',
] as const;
export type SharedProfileKey = (typeof SHARED_PROFILE_KEYS)[number];

export type ConditionOp =
  | 'equals'
  | 'not_equals'
  | 'contains_any'
  | 'empty'
  | 'not_empty'
  | 'gt'
  | 'gte'
  | 'lt'
  | 'lte';

export interface Condition {
  /** Stable field id. Inside a repeater row, sibling child ids resolve to the row value first. */
  field: string;
  op: ConditionOp;
  /** Literal comparison value; for selects this is the option id (or ids for contains_any). */
  value?: string | number | boolean | string[];
}

export interface ConditionGroup {
  all?: Array<Condition | ConditionGroup>;
  any?: Array<Condition | ConditionGroup>;
}

export interface FieldOption {
  /** Stable option id — never reused for a different meaning. */
  id: string;
  /** Semantic key (e.g. provider key `gsc`). */
  key: string;
  label: string;
  /** Selecting this option clears/blocks all other options (None / Not sure). */
  exclusive?: boolean;
  /** Selecting this option reveals a detail text input (Other). */
  opensDetail?: boolean;
  retired?: boolean;
}

export interface FieldValidation {
  minLength?: number;
  maxLength?: number;
  min?: number;
  max?: number;
  minItems?: number;
  maxItems?: number;
  minRows?: number;
  maxRows?: number;
  /** Currency codes allowed for currency fields (ISO 4217). */
  currencies?: string[];
  /** File upload: allowed categories of content. */
  accept?: Array<'image' | 'video' | 'document' | 'archive' | 'font'>;
  maxFileSizeMb?: number;
  maxFiles?: number;
}

export type FieldAudience = 'shared' | 'internal';

export interface FieldDef {
  id: string;
  key: string;
  type: FieldType;
  label: string;
  help?: string;
  placeholder?: string;
  /** Traceability to the numbered source section/label (e.g. `F01.02`). */
  sourceRef?: string;
  required?: boolean;
  requiredWhen?: ConditionGroup;
  visibleWhen?: ConditionGroup;
  options?: FieldOption[];
  allowUnknown?: boolean;
  allowNa?: boolean;
  validation?: FieldValidation;
  /**
   * Answer value pre-filled when a form instance is created (see forms/defaults.ts). Never
   * overwrites an existing/prefilled answer; not allowed on internal fields, repeater children,
   * acknowledgements, signatures, uploads or links. Versioned with the template like any other property.
   */
  defaultValue?: unknown;
  sensitivity?: Sensitivity;
  /** internal fields are never sent to client APIs. */
  audience?: FieldAudience;
  /** Who may edit. Defaults to both. */
  editableBy?: Array<'client' | 'staff'>;
  profileKey?: SharedProfileKey;
  /** Repeater children. */
  children?: FieldDef[];
  rowLabel?: string;
  /** Links to evidence registers. */
  assetCategory?: string;
  accessProvider?: string;
  /** For acknowledgement/info: exact wording, versioned by the template version. */
  text?: string;
  /** For date fields: 'past' | 'future' constraints */
  dateConstraint?: 'past' | 'future';
}

export interface SectionDef {
  id: string;
  key: string;
  /** Source numbering, e.g. `F01.03`. */
  number?: string;
  title: string;
  description?: string;
  instructions?: string[];
  visibleWhen?: ConditionGroup;
  fields: FieldDef[];
}

export interface ConfirmationBlockDef {
  /** e.g. C01 */
  code: string;
  textVersion: string;
  text: string;
}

export interface TemplateDefinition {
  /** Semantic library key: seo_onboarding, gbp_onboarding ... */
  key: string;
  /** Source code e.g. F01 */
  code?: string;
  name: string;
  description?: string;
  serviceModule?: string;
  sections: SectionDef[];
  confirmation?: ConfirmationBlockDef;
  /** Config clarifications that must be set before publication (e.g. bi-weekly semantics). */
  settings?: {
    biweeklyMeaning?: 'every_two_weeks' | 'twice_a_month';
  };
}

/* ---------------------------------------------------------------- answers */

/**
 * Explicit answer states. `blank` is represented by absence (or kind 'blank').
 * false and 0 are genuine values and never treated as blank.
 */
export type AnswerValue =
  | { kind: 'blank' }
  | { kind: 'value'; value: unknown }
  | { kind: 'unknown' }
  | { kind: 'na'; reason?: string }
  | { kind: 'waived'; waiverId: string };

export interface RepeaterRow {
  id: string;
  values: Record<string, AnswerValue>;
  /** Soft delete preserves historical rows in earlier revisions. */
  deletedAt?: string | null;
  createdAt?: string;
}

export interface RepeaterValue {
  rows: RepeaterRow[];
}

export interface SelectValue {
  /** Option ids */
  selected: string[];
  /** Detail text keyed by option id (for opensDetail options) */
  details?: Record<string, string>;
}

export interface CurrencyValue {
  amount: number;
  currency: string;
}

export interface TimeInterval {
  open: string; // HH:mm
  close: string; // HH:mm ; close <= open means overnight
}

export const WEEK_DAYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'] as const;
export type WeekDay = (typeof WEEK_DAYS)[number];

export interface DayHours {
  closed: boolean;
  intervals: TimeInterval[];
}

export interface HolidayOverride {
  id: string;
  date: string; // YYYY-MM-DD
  label?: string;
  closed: boolean;
  intervals: TimeInterval[];
}

export interface WeeklyHoursValue {
  timezone: string;
  days: Record<WeekDay, DayHours>;
  overrides?: HolidayOverride[];
}

export interface FileRefValue {
  assetIds: string[];
}

export interface AssetLinkValue {
  url: string;
  label?: string;
}

export interface SignatureValue {
  /** Typed full name used as acknowledgement signature */
  name: string;
  signedAt?: string;
}

export type AnswerMap = Record<string, AnswerValue>;

export interface FieldChange {
  field_id: string;
  row_id: string | null;
  /** Child field id when row_id is set */
  child_field_id?: string | null;
  value: AnswerValue | null;
  /** Row operations for repeaters */
  row_op?: 'add' | 'delete' | 'restore';
}
